import { Copy, Loader2, Lock } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";

interface ActiveContextReadOnlyBannerProps {
  variant?: "inline" | "modal";
  className?: string;
  onDuplicate?: () => void;
  isDuplicating?: boolean;
  /** When false, Duplicate stays visible but disabled. */
  canDuplicate?: boolean;
}

export const ActiveContextReadOnlyBanner = ({
  variant = "inline",
  className,
  onDuplicate,
  isDuplicating = false,
  canDuplicate = true,
}: ActiveContextReadOnlyBannerProps) => {
  const showDuplicate = onDuplicate != null;
  const duplicateDisabled = isDuplicating || !canDuplicate;

  const duplicateButton = showDuplicate ? (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={onDuplicate}
      disabled={duplicateDisabled}
      className="h-7 gap-1.5 px-2.5 text-xs shrink-0 border-amber-500/30 bg-background/80 hover:bg-background disabled:cursor-not-allowed"
    >
      {isDuplicating ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
      {isDuplicating ? "Duplicating…" : "Duplicate"}
    </Button>
  ) : null;

  if (variant === "modal") {
    return (
      <div
        className={cn(
          "ml-6 mr-12 mb-2 flex items-start gap-3 rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-4 py-3.5 shrink-0",
          className
        )}
      >
        <Lock className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-foreground leading-snug">
            This context is active and read-only
          </p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Active contexts can't be edited to keep your board consistent. To make changes,
            duplicate this context.
          </p>
        </div>
        {duplicateButton}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "mx-6 mt-4 flex items-center gap-3 rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2",
        className
      )}
    >
      <Lock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
      <p className="text-xs text-muted-foreground leading-snug flex-1 min-w-0">
        <span className="font-medium text-foreground">Read-only.</span>{" "}
        Active contexts can't be edited. Duplicate this context to make changes.
      </p>
      {duplicateButton}
    </div>
  );
};
